import type { IContent } from '~/features-shared/content/interfaces/content'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import { logger } from '~/features-shared/libs/utils/logger'
/**
 * Map a subjectUri with a Content (post, idea, wiki, wedo, etc...)
 */
export const contentsMap: Map<SUBJECT_6W_URI, IContent> = reactive(new Map())

/**
 * Merge a content with an existing content in the contents map.
 * If the content do not exist, it just add this new content in the map.
 * @param contentUpdated The content to add/merge
 * @param force If true, it will force the merge of the content into the map
 * @returns the merged content
 */
// eslint-disable-next-line complexity, max-statements
export function mergeContent(contentUpdated: Partial<IContent>, force = false) {
  if (!contentUpdated.subject_uri)
    return contentUpdated

  const key = contentUpdated.subject_uri
  const contentStored = contentsMap.get(key)

  // no content already stored, store it and return
  if (!contentStored) {
    contentsMap.set(key, contentUpdated as IContent)
    return contentUpdated
  }

  // we force an update, acl should be updated
  if (force && contentUpdated._acl)
    contentsMap.set(key, Object.assign<IContent, Partial<IContent>>(contentStored, { _acl: contentUpdated._acl }))

  // last_contributed_event can be always updated
  if (contentUpdated.last_contributed_event) {
    contentsMap.set(key, Object.assign<IContent, Partial<IContent>>(contentStored, {
      last_contributed_event: contentUpdated.last_contributed_event,
      last_contributed_at: contentUpdated.last_contributed_at,
      last_contributed_author: contentUpdated.last_contributed_author,
    }))
  }

  // published_at can't be empty
  if (contentUpdated.model === IContentModel.Media && contentUpdated.published_at === '') {
    Object.assign<Partial<IContent>, Partial<IContent>>(contentUpdated, {
      ...contentUpdated,
      published_at: contentStored.published_at,
    })
  }

  /**
   * If we already have a more recent content in the store, don't merge it to keep the most updated data.
   * The comparison of dates ensures that the merge happens only if the received
   * content has an "updated_at" date "after" the currently stored content's updated_at date.
   * Except for media, need to merge content when preview_fingerprint is different (updated_at can be the same).
   * This also means there are not many merge operations happening unnecessarily.
   * "force" flag is used to merge the content forcefully. We usually need it when we call the backend for
   * details of a single content (like detailed view).
   */

  /** Check if the stored content was updated more recently */
  const upperUpdatedAt = contentStored.updated_at
    && contentUpdated.updated_at
    // if creation date is different than update date
    && (contentUpdated.created_at && contentUpdated.created_at !== contentStored.updated_at)
    // and stored content date is greater than updated content
    && new Date(contentStored.updated_at) > new Date(contentUpdated.updated_at)

  /** Check if the stored content was updated more recently or has the same update date */
  const upperOrEqualUpdatedAt = contentStored.updated_at && contentUpdated.updated_at
    && new Date(contentStored.updated_at) >= new Date(contentUpdated.updated_at)

  /** Check if the preview fingerprint are the same */
  const samePreviewFingerprint = contentStored
    && contentStored.preview_fingerprint
    && contentUpdated.preview_fingerprint
    && contentStored.preview_fingerprint === contentUpdated.preview_fingerprint

  // If !force and the store content is more recent, skip
  // If force but the store content is more recent, skip
  if ((!force && upperOrEqualUpdatedAt && samePreviewFingerprint) || (force && upperUpdatedAt)) {
    logger.info('More recent content already stored. Skipping update of %s.', key)
    return contentUpdated
  }

  // Merge the new content to already existing content (if it exists)
  contentsMap.set(key, Object.assign<IContent, Partial<IContent>>(contentStored, contentUpdated))
  return contentStored
}
